import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

const TOKEN_KEY = 'cs_attendee_token'
const SLUG_KEY  = 'cs_attendee_slug'
const NAME_KEY  = 'cs_attendee_name'

export const attendeeAuth = {
  token: () => localStorage.getItem(TOKEN_KEY),
  slug:  () => localStorage.getItem(SLUG_KEY),
  name:  () => localStorage.getItem(NAME_KEY),
  isAuthed: () => !!localStorage.getItem(TOKEN_KEY),
  setSession: (token: string, name: string) => {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(NAME_KEY, name)
  },
  rememberSlug: (slug: string) => localStorage.setItem(SLUG_KEY, slug),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(NAME_KEY)
  },
}

// Authenticated instance for portal endpoints
const portal = axios.create({ baseURL: BASE_URL })
portal.interceptors.request.use(config => {
  const t = attendeeAuth.token()
  if (t) config.headers.Authorization = `Bearer ${t}`
  return config
})
portal.interceptors.response.use(
  r => r,
  err => {
    if (err?.response?.status === 401) attendeeAuth.clear()
    return Promise.reject(err)
  },
)

// Unauthenticated instance for the magic-link flow
const anon = axios.create({ baseURL: BASE_URL })

export type AttendeeProfile = {
  id: string
  fullName: string
  email: string
  phone: string | null
  companyName: string | null
  marketingConsent: boolean
  region: string
  timeZoneId: string
  photoUpdatedAt: string | null
}

export type PhotoSettings = { enabled: boolean; notice: string | null; showOnVerification: boolean }

export type AttendeePortalBooking = {
  sessionId: string
  courseName: string
  startsAt: string
  venueName: string | null
  bookingStatus: string
  attendanceStatus: string
}

export type AttendeePortalCertificate = {
  certificateId: string
  courseName: string
  issuedAt: string
}

export const attendeeApi = {
  // Auth (anonymous)
  requestLink: (slug: string, email: string) =>
    anon.post('/attendee/auth/request-link', { slug, email }),
  exchange: (token: string) =>
    anon.post<{ accessToken: string; attendeeName: string }>('/attendee/auth/exchange', { token }),

  // Portal (authenticated)
  getMe: () => portal.get<AttendeeProfile>('/attendee/me'),
  updateMe: (data: { fullName: string; phone: string | null; companyName: string | null; marketingConsent: boolean }) =>
    portal.patch('/attendee/me', data),
  getPhotoSettings: () => portal.get<PhotoSettings>('/attendee/photo-settings'),
  getPhoto: () => portal.get<Blob>('/attendee/photo', { responseType: 'blob' }),
  setPhoto: (photo: Blob) => { const f = new FormData(); f.append('file', photo, 'photo.jpg'); return portal.post('/attendee/photo', f) },
  removePhoto: () => portal.delete('/attendee/photo'),
  getBookings: () => portal.get<AttendeePortalBooking[]>('/attendee/bookings'),
  getCertificates: () => portal.get<AttendeePortalCertificate[]>('/attendee/certificates'),
}
